import api from './api';

export interface Formation {
  id: number;
  titre: string;
  description: string;
  objectifs: string;
  publicCible: string;
  programme: string;
  intervenants: string;
  prerequis: string;
  duree: number;
  format: 'presentiel' | 'en_ligne' | 'hybride';
  prix: number;
  statut: 'active' | 'inactive' | 'archivee' | 'en_attente';
  fichierProgramme?: string;
  visuel?: string;
  motsCles: string[];
  specialites: string[];
  nombreVues: number;
  noteMoyenne: number;
  nombreAvis: number;
  centre: {
    id: number;
    nom: string;
    logo?: string;
    description: string;
  };
  sessions: Session[];
  commentaires: Commentaire[];
  createdAt: string;
  updatedAt: string;
}

export interface Session {
  id: number;
  titre: string;
  description?: string;
  dateDebut: string;
  dateFin: string;
  nombrePlaces: number;
  nombreInscrits: number;
  lieu?: string;
  lienVisio?: string;
  statut: 'planifiee' | 'en_cours' | 'terminee' | 'annulee';
  prixSession?: number;
  notes?: string;
}

export interface Commentaire {
  id: number;
  contenu: string;
  note: number;
  statut: 'en_attente' | 'approuve' | 'rejete';
  nomAuteur?: string;
  estAnonyme: boolean;
  reponseCentre?: string;
  dateReponse?: string;
  createdAt: string;
}

export interface CentreFormation {
  id: number;
  nom: string;
  description: string;
  logo?: string;
  siteWeb?: string;
  facebook?: string;
  linkedin?: string;
  twitter?: string;
  domainesIntervention: string[];
  specialites: string[];
  statut: 'actif' | 'inactif' | 'en_attente' | 'suspendu';
  estVerifie: boolean;
  nombreFormations: number;
  nombreInscriptions: number;
  noteMoyenne: number;
  nombreAvis: number;
  adresse?: {
    id: number;
    rue: string;
    commune: {
      id: number;
      nom: string;
      wilaya: {
        id: number;
        nom: string;
      };
    };
  };
}

export interface Inscription {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonction?: string;
  entreprise?: string;
  diplome?: string;
  commentaires?: string;
  statut: 'en_attente' | 'validee' | 'refusee' | 'annulee' | 'payee';
  statutPaiement: 'en_attente' | 'paye' | 'echec' | 'rembourse';
  montantPaye?: number;
  referencePaiement?: string;
  datePaiement?: string;
  factureUrl?: string;
  formation: Formation;
  session: Session;
  createdAt: string;
}

export interface FormationFilters {
  format?: string;
  specialites?: string[];
  motsCles?: string[];
  centreId?: number;
  prixMin?: number;
  prixMax?: number;
  dureeMin?: number;
  dureeMax?: number;
}

export interface CentreFilters {
  statut?: string;
  specialites?: string[];
  region?: string;
}

class FormationsService {
  // Formations
  async getFormations(filters?: FormationFilters): Promise<Formation[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await api.get(`/formations?${params}`);
    return response.data;
  }

  async getFormation(id: number): Promise<Formation> {
    const response = await api.get(`/formations/${id}`);
    return response.data;
  }

  async createFormation(data: Partial<Formation>): Promise<Formation> {
    const response = await api.post('/formations', data);
    return response.data;
  }

  async updateFormation(id: number, data: Partial<Formation>): Promise<Formation> {
    const response = await api.patch(`/formations/${id}`, data);
    return response.data;
  }

  async deleteFormation(id: number): Promise<void> {
    await api.delete(`/formations/${id}`);
  }

  // Sessions
  async getSessions(formationId: number): Promise<Session[]> {
    const response = await api.get(`/formations/${formationId}/sessions`);
    return response.data;
  }

  async createSession(formationId: number, data: Partial<Session>): Promise<Session> {
    const response = await api.post(`/formations/${formationId}/sessions`, data);
    return response.data;
  }

  // Commentaires
  async getCommentaires(formationId: number): Promise<Commentaire[]> {
    const response = await api.get(`/formations/${formationId}/commentaires`);
    return response.data;
  }

  async createCommentaire(formationId: number, data: Partial<Commentaire>): Promise<Commentaire> {
    const response = await api.post(`/formations/${formationId}/commentaires`, data);
    return response.data;
  }

  async updateCommentaireStatus(id: number, statut: string): Promise<Commentaire> {
    const response = await api.patch(`/formations/commentaires/${id}/status`, { statut });
    return response.data;
  }

  // Centres de formation
  async getCentres(filters?: CentreFilters): Promise<CentreFormation[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await api.get(`/formations/centres?${params}`);
    return response.data;
  }

  async getCentre(id: number): Promise<CentreFormation> {
    const response = await api.get(`/formations/centres/${id}`);
    return response.data;
  }

  async createCentre(data: Partial<CentreFormation>): Promise<CentreFormation> {
    const response = await api.post('/formations/centres', data);
    return response.data;
  }

  // Inscriptions
  async createInscription(data: Partial<Inscription>): Promise<Inscription> {
    const response = await api.post('/formations/inscriptions', data);
    return response.data;
  }

  async getCentreInscriptions(): Promise<Inscription[]> {
    const response = await api.get('/formations/centre/inscriptions');
    return response.data;
  }

  async updateInscriptionStatus(id: number, statut: string): Promise<Inscription> {
    const response = await api.patch(`/formations/inscriptions/${id}/status`, { statut });
    return response.data;
  }

  // Statistiques
  async getFormationStats(formationId: number): Promise<any> {
    const response = await api.get(`/formations/${formationId}/stats`);
    return response.data;
  }

  async getCentreStats(): Promise<any> {
    const response = await api.get('/formations/centre/stats');
    return response.data;
  }

  async getGlobalStats(): Promise<any> {
    const response = await api.get('/formations/admin/stats');
    return response.data;
  }
}

export const formationsService = new FormationsService(); 